import React, { useState, useEffect } from 'react';
import { getApiBaseUrl } from '../config/api';
import './AgentPasswordManagement.css';

const AgentPasswordManagement = ({ user, onShowNotification }) => {
  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  
  const API_BASE_URL = getApiBaseUrl();

  useEffect(() => {
    loadAgents();
  }, [currentPage, searchTerm]);

  const loadAgents = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString()
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/agents?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setAgents(result.agents || []);
          setPagination(result.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 });
        }
      }
    } catch (error) {
      console.error('Error loading agents:', error);
      if (onShowNotification) {
        onShowNotification('Error loading agents. Please try again.', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedAgent) return;
    
    if (!resetPassword || resetPassword.length < 6) {
      if (onShowNotification) {
        onShowNotification('Password must be at least 6 characters long', 'error');
      }
      return;
    }
    
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/agents/${selectedAgent.id}/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          newPassword: resetPassword
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setResetResult({
            type: 'single',
            agent: result.agent,
            password: result.password,
            message: result.message
          });
          setResetPassword('');
          setShowResetModal(false);
          if (onShowNotification) {
            onShowNotification(`Password reset for ${result.agent.agency_name}`, 'success');
          }
        }
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Failed to reset password' }));
        throw new Error(errorData.error || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      if (onShowNotification) {
        onShowNotification(error.message || 'Error resetting password. Please try again.', 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      if (onShowNotification) {
        onShowNotification('Password copied to clipboard', 'success');
      }
    });
  };

  return (
    <div className="agent-password-management">
      <div className="password-header">
        <div className="header-left">
          <h2>🔑 Agent Password Management</h2>
          <p>Reset passwords for individual agents</p>
        </div>
        <div className="header-actions">
          <input
            type="text"
            placeholder="Search agents..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="search-input"
          />
        </div>
      </div>

      {resetResult && (
        <div className={`reset-result ${resetResult.type}`}>
          <div className="result-content">
            <h3>✅ Password Reset Successful</h3>
            {resetResult.type === 'single' ? (
              <>
                <p><strong>Agent:</strong> {resetResult.agent.agency_name}</p>
                <p><strong>Username:</strong> {resetResult.agent.username}</p>
                <p><strong>Email:</strong> {resetResult.agent.email}</p>
                <div className="password-display">
                  <strong>New Password:</strong>
                  <div className="password-box">
                    <code>{resetResult.password}</code>
                    <button onClick={() => copyToClipboard(resetResult.password)}>📋 Copy</button>
                  </div>
                  <small>⚠️ Save this password securely. It will not be shown again.</small>
                </div>
              </>
            ) : null}
            <button className="btn-secondary" onClick={() => setResetResult(null)}>Close</button>
          </div>
        </div>
      )}

      <div className="agents-table-container">
        {isLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading agents...</p>
          </div>
        ) : agents.length === 0 ? (
          <div className="no-data">No agents found</div>
        ) : (
          <>
            <table className="agents-table">
              <thead>
                <tr>
                  <th>Agency Name</th>
                  <th>IRA Number</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {agents.map((agent) => (
                  <tr key={agent.id}>
                    <td>{agent.agency_name}</td>
                    <td>{agent.ira_number}</td>
                    <td>{agent.username}</td>
                    <td>{agent.email || 'N/A'}</td>
                    <td>
                      <span className={`status-badge ${agent.is_active ? 'active' : 'inactive'}`}>
                        {agent.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn-reset"
                        onClick={() => {
                          setSelectedAgent(agent);
                          setResetPassword('');
                          setShowResetModal(true);
                        }}
                        disabled={!agent.is_active}
                      >
                        🔑 Reset Password
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {pagination.totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span>Page {currentPage} of {pagination.totalPages}</span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(pagination.totalPages, p + 1))}
                  disabled={currentPage === pagination.totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Reset Single Agent Modal */}
      {showResetModal && selectedAgent && (
        <div className="modal-overlay" onClick={() => !isSubmitting && setShowResetModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reset Password for {selectedAgent.agency_name}</h3>
              <button className="modal-close" onClick={() => setShowResetModal(false)} disabled={isSubmitting}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Agent Information:</label>
                <div className="info-box">
                  <p><strong>IRA Number:</strong> {selectedAgent.ira_number}</p>
                  <p><strong>Username:</strong> {selectedAgent.username}</p>
                  <p><strong>Email:</strong> {selectedAgent.email || 'N/A'}</p>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="resetPassword">New Password:</label>
                <input
                  type="password"
                  id="resetPassword"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  className="form-input"
                  disabled={isSubmitting}
                />
                <small>Leave empty to generate a random password</small>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowResetModal(false)}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleResetPassword}
                disabled={isSubmitting || (resetPassword && resetPassword.length < 6)}
              >
                {isSubmitting ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentPasswordManagement;

